import {
  inventoryLocationDisplayName,
  type InventoryLocationSnapshot,
} from "./inventory-snapshot";

export const INVENTORY_TRANSFER_REQUEST_LOCATION = "大阪";
export const INVENTORY_TRANSFER_DEFAULT_RECEIVING_LOCATION = "東京";

export type InventoryTransferView = "replenishment" | "relocation" | "active" | "history";

export interface InventoryTransferStatusRecord {
  processingStatus: string;
}

function isRequestedInventoryTransfer(record: InventoryTransferStatusRecord): boolean {
  return record.processingStatus.normalize("NFKC").trim() !== "未依頼";
}

export function inventoryTransferViewRecords<T extends InventoryTransferStatusRecord>(
  records: readonly T[],
  view: InventoryTransferView,
): T[] {
  if (view === "replenishment" || view === "relocation") return [];
  return records.filter((record) =>
    isRequestedInventoryTransfer(record)
      && (view === "history"
        ? record.processingStatus === "出荷済み"
        : record.processingStatus !== "出荷済み")
  );
}

export function inventoryTransferViewCounts(
  records: readonly InventoryTransferStatusRecord[],
): { replenishment: number; active: number; history: number } {
  let history = 0;
  let active = 0;
  for (const record of records) {
    if (!isRequestedInventoryTransfer(record)) continue;
    if (record.processingStatus === "出荷済み") history += 1;
    else active += 1;
  }
  return { replenishment: 0, active, history };
}

export function shouldShowInventoryTransferRequestStatus(
  workspace: string,
  view: string,
  filter: string,
): boolean {
  return workspace === "all" && view === "history" && filter === "received";
}

export interface InventoryTransferSelectableItem {
  quantity: number;
  currentQuantity: number;
  inventoryLocations?: InventoryLocationSnapshot[];
}

export function inventoryTransferMaxQuantity(item: InventoryTransferSelectableItem): number {
  return Math.max(0, item.currentQuantity >= 0 ? item.currentQuantity : item.quantity);
}

export function inventoryTransferReceivingLocations(
  items: readonly InventoryTransferSelectableItem[],
): string[] {
  const locations = new Set<string>([INVENTORY_TRANSFER_DEFAULT_RECEIVING_LOCATION]);
  for (const item of items) {
    for (const location of item.inventoryLocations ?? []) {
      const name = inventoryLocationDisplayName(location.name);
      if (name && name !== INVENTORY_TRANSFER_REQUEST_LOCATION) locations.add(name);
    }
  }
  return [...locations].sort((left, right) => {
    if (left === INVENTORY_TRANSFER_DEFAULT_RECEIVING_LOCATION) return -1;
    if (right === INVENTORY_TRANSFER_DEFAULT_RECEIVING_LOCATION) return 1;
    return left.localeCompare(right, "ja");
  });
}

export function inventoryTransferSharedMaxQuantity(maxQuantities: readonly number[]): number {
  const positive = maxQuantities.filter((quantity) => Number.isInteger(quantity) && quantity > 0);
  return positive.length > 0 ? Math.min(...positive) : 0;
}
